import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useReaderCredits } from '../contexts/ReaderCreditContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, BookOpen, CreditCard, AlertCircle } from 'lucide-react';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

/**
 * Payment Success Page
 * 
 * This page handles the return from Stripe Checkout.
 * It polls the checkout status to confirm payment completion.
 * 
 * Flow:
 * 1. User completes Stripe Checkout
 * 2. Stripe redirects to this page with session_id
 * 3. We poll /api/nfm/checkout/status/{session_id}
 * 4. Display success once payment_status is 'paid'
 */
export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshCreditsSummary } = useReaderCredits();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID provided');
      return;
    }
    
    const settleAndCheckStatus = async () => {
      try {
        // First, attempt to settle the transaction
        // This is idempotent - if already settled, it returns success
        const settleResponse = await fetch(
          `${API_URL}/api/nfm/checkout/settle/${sessionId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (settleResponse.ok) {
          const settleData = await settleResponse.json();
          
          if (settleData.status === 'settled' || settleData.status === 'already_settled') {
            setStatus('success');
            setPaymentData({
              ...settleData,
              product_type: settleData.product_type,
              product_id: settleData.product_id,
              amount_cents: null // Will be fetched from status if needed
            });
            
            // Refresh credit balance in UI
            if (refreshCreditsSummary) {
              await refreshCreditsSummary();
            }
            return;
          } else if (settleData.status === 'payment_not_complete') {
            // Payment not yet complete, poll status
            pollStatus();
            return;
          }
        }
        
        // Fall back to polling if settle fails
        pollStatus();
        
      } catch (err) {
        console.error('Settlement error:', err);
        // Fall back to polling
        pollStatus();
      }
    };
    
    const pollStatus = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/nfm/checkout/status/${sessionId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }
        
        const data = await response.json();
        setPaymentData(data);
        
        if (data.payment_status === 'paid' && data.status === 'complete') {
          // Try to settle again now that payment is complete
          settleAndCheckStatus();
        } else if (data.status === 'expired') {
          setStatus('error');
          setError('Payment session has expired');
        } else if (pollCount < 10) {
          // Poll again if not complete (max 10 times, 2s apart = 20s max)
          setPollCount(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          // After 10 polls, show manual check message
          setStatus('pending');
        }
      } catch (err) {
        console.error('Payment status check error:', err);
        setStatus('error');
        setError(err.message);
      }
    };
    
    settleAndCheckStatus();
  }, [sessionId, refreshCreditsSummary]);
  
  const getProductName = () => {
    if (!paymentData) return 'your purchase';
    const type = paymentData.product_type;
    const id = paymentData.product_id;
    
    if (!id) return 'your purchase';
    
    if (type === 'subscription') {
      // Handle new plan_id format: novice_monthly, pro_reader_annual, etc.
      const planName = id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return `${planName} Subscription`;
    }
    
    // Handle bundle format: booster_pack, power_pack, etc.
    const bundleName = id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return `${bundleName}`;
  };
  
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-slate-900 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-16 w-16 text-purple-500 animate-spin mx-auto mb-6" />
            <h1 className="text-xl font-semibold text-white mb-2">
              Processing Your Payment
            </h1>
            <p className="text-slate-400">
              Please wait while we confirm your purchase...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-slate-900 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-xl font-semibold text-white mb-2">
              Payment Error
            </h1>
            <p className="text-slate-400 mb-6">
              {error || 'Something went wrong with your payment'}
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full bg-purple-600 hover:bg-purple-500"
              >
                Try Again
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/reader/my-library')}
                className="text-slate-400 hover:text-white"
              >
                Go to Library
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-slate-900 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-16 w-16 text-amber-500 mx-auto mb-6" />
            <h1 className="text-xl font-semibold text-white mb-2">
              Payment Processing
            </h1>
            <p className="text-slate-400 mb-6">
              Your payment is being processed. This may take a moment.
              Check your transaction history for confirmation.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/account/transactions')}
                className="w-full bg-amber-600 hover:bg-amber-500"
              >
                View Transaction History
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/reader/my-library')}
                className="text-slate-400 hover:text-white"
              >
                Go to Library
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Success state
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <Card className="max-w-md w-full bg-slate-900 border-slate-700">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">
            Purchase Confirmed!
          </h1>
          
          <p className="text-slate-400 mb-6">
            Thank you for your purchase of {getProductName()}.
            Your credits have been added to your account.
          </p>
          
          {paymentData?.amount_cents && (
            <div className="bg-slate-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-400">Amount Paid</p>
              <p className="text-2xl font-bold text-emerald-400">
                ${(paymentData.amount_cents / 100).toFixed(2)}
              </p>
            </div>
          )}
          
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate('/reader/my-library')}
              className="w-full bg-purple-600 hover:bg-purple-500"
              data-testid="go-to-library-btn"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Go to Library
            </Button>
            
            <Button
              variant="outline"
              onClick={() => navigate('/account/transactions')}
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="view-transactions-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              View Transaction History
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
